/**
 * Live agent state (Zustand, not persisted). The server transcript is the truth;
 * this store folds SSE events into what the panel renders: streaming text,
 * tool cards, approval prompts, provider banner and the context/cost meter.
 */
import { create } from 'zustand';
import type { AgentEvent, AgentMessageRecord, AgentSessionSummary, ContentBlock, ProviderId, SessionStatus, ToolCallRecord } from '@cortex/shared';
import { Api } from '../lib/api';

export interface Turn {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  /** Tool calls issued by this assistant turn, in order. */
  calls: string[];
  streaming: boolean;
  provider?: ProviderId | null;
  createdAt: string;
  thinking?: string;
}

export interface Banner {
  kind: 'switched' | 'error' | 'info';
  text: string;
}

interface AgentState {
  sessionId: string | null;
  session: AgentSessionSummary | null;
  status: SessionStatus;
  turns: Turn[];
  calls: Record<string, ToolCallRecord>;
  pendingApprovals: string[];
  banners: Banner[];
  usage: { tokensIn: number; tokensOut: number; costCents: number; contextTokens: number; contextBudget: number } | null;
  citations: Record<string, Array<{ target: string; kind: 'note' | 'web' | 'tool' }>>;
  connected: boolean;
  seq: number;
  caseRef: string | null;
  draft: string;
  mode: 'ASSIST' | 'FAULTFIND' | 'DESIGN' | 'RESEARCH' | 'SCRIBE';

  setDraft(s: string): void;
  setMode(m: AgentState['mode']): void;
  load(id: string): Promise<void>;
  reset(): void;
  applyEvent(seq: number, ev: AgentEvent): void;
  setConnected(b: boolean): void;
  dismissBanner(i: number): void;
}

const EMPTY = {
  sessionId: null,
  session: null,
  status: 'IDLE' as SessionStatus,
  turns: [] as Turn[],
  calls: {} as Record<string, ToolCallRecord>,
  pendingApprovals: [] as string[],
  banners: [] as Banner[],
  usage: null,
  citations: {} as AgentState['citations'],
  connected: false,
  seq: 0,
  caseRef: null as string | null,
};

function fromRecord(m: AgentMessageRecord): Turn {
  const text = m.content.filter((b): b is Extract<ContentBlock, { type: 'text' }> => b.type === 'text').map((b) => b.text).join('\n');
  const calls = m.content.filter((b): b is Extract<ContentBlock, { type: 'tool_use' }> => b.type === 'tool_use').map((b) => b.id);
  return { id: m.id, role: m.role, text, calls, streaming: false, provider: m.provider ?? null, createdAt: m.createdAt };
}

export const useAgent = create<AgentState>()((set, get) => ({
  ...EMPTY,
  draft: '',
  mode: 'ASSIST',

  setDraft: (draft) => set({ draft }),
  setMode: (mode) => set({ mode }),
  reset: () => set({ ...EMPTY }),

  async load(id) {
    const d = await Api.agent.get(id);
    const calls: Record<string, ToolCallRecord> = {};
    for (const c of d.calls) calls[c.id] = c;
    // Tool-result-only user messages are plumbing, not conversation.
    const turns = d.messages.filter((m) => !(m.role === 'user' && m.content.every((b) => b.type === 'tool_result'))).map(fromRecord);
    const { messages: _m, calls: _c, ...summary } = d;
    set({
      ...EMPTY,
      sessionId: id,
      session: summary,
      status: d.status,
      turns,
      calls,
      pendingApprovals: d.calls.filter((c) => c.status === 'AWAITING_APPROVAL').map((c) => c.id),
      caseRef: (() => { const cc = [...d.calls].reverse().find((c) => c.tool === 'case.create' && c.status === 'OK'); const closed = d.calls.some((c) => c.tool === 'case.close' && c.status === 'OK'); return cc && !closed ? String((cc.output as { ref?: string } | null)?.ref ?? '') || null : null; })(),
      usage: { tokensIn: d.tokensIn, tokensOut: d.tokensOut, costCents: d.costCents, contextTokens: 0, contextBudget: 0 },
      mode: d.mode,
    });
  },

  setConnected: (connected) => set({ connected }),
  dismissBanner: (i) => set({ banners: get().banners.filter((_, j) => j !== i) }),

  applyEvent(seq, ev) {
    if (seq <= get().seq) return; // replayed
    const s = get();
    const patch: Partial<AgentState> = { seq };
    const turns = [...s.turns];
    const last = turns[turns.length - 1];
    const ensureStreaming = (messageId: string): Turn => {
      if (last && last.role === 'assistant' && last.streaming) return last;
      const t: Turn = { id: messageId, role: 'assistant', text: '', calls: [], streaming: true, createdAt: new Date().toISOString() };
      turns.push(t);
      return t;
    };
    switch (ev.type) {
      case 'text_delta': {
        const t = ensureStreaming(ev.messageId);
        turns[turns.length - 1] = { ...t, text: t.text + ev.text };
        patch.turns = turns;
        break;
      }
      case 'thinking': {
        const t = ensureStreaming(ev.messageId);
        turns[turns.length - 1] = { ...t, thinking: (t.thinking ?? '') + ev.text };
        patch.turns = turns;
        break;
      }
      case 'tool_start':
      case 'tool_end':
      case 'approval_required': {
        patch.calls = { ...s.calls, [ev.call.id]: ev.call };
        const t = last && last.role === 'assistant' ? last : ensureStreaming(ev.call.id);
        if (!t.calls.includes(ev.call.id)) turns[turns.length - 1] = { ...t, calls: [...t.calls, ev.call.id] };
        patch.turns = turns;
        if (ev.type === 'approval_required') patch.pendingApprovals = [...s.pendingApprovals.filter((x) => x !== ev.call.id), ev.call.id];
        if (ev.type === 'tool_end') patch.pendingApprovals = s.pendingApprovals.filter((x) => x !== ev.call.id);
        if (ev.type === 'tool_end' && ev.call.tool === 'case.create' && ev.call.status === 'OK') patch.caseRef = String((ev.call.output as { ref?: string } | null)?.ref ?? '') || s.caseRef;
        if (ev.type === 'tool_end' && ev.call.tool === 'case.close' && ev.call.status === 'OK') patch.caseRef = null;
        break;
      }
      case 'message_end': {
        const rec = fromRecord(ev.message);
        if (last && last.role === 'assistant' && last.streaming) {
          turns[turns.length - 1] = { ...rec, calls: [...new Set([...last.calls, ...rec.calls])], thinking: last.thinking };
        } else turns.push(rec);
        patch.turns = turns;
        break;
      }
      case 'citation': {
        const list = s.citations[ev.messageId] ?? [];
        if (!list.some((c) => c.target === ev.target)) patch.citations = { ...s.citations, [ev.messageId]: [...list, { target: ev.target, kind: ev.kind }] };
        break;
      }
      case 'provider_switched':
        patch.banners = [...s.banners, { kind: 'switched', text: `Switched from ${ev.from} to ${ev.to}: ${ev.reason}. Answers from here are from the local model.` }];
        patch.session = s.session ? { ...s.session, provider: ev.to, degraded: true } : s.session;
        break;
      case 'usage':
        patch.usage = { tokensIn: ev.tokensIn, tokensOut: ev.tokensOut, costCents: ev.costCents, contextTokens: ev.contextTokens, contextBudget: ev.contextBudget };
        break;
      case 'status':
        patch.status = ev.status;
        break;
      case 'error':
        patch.banners = [...s.banners, { kind: ev.fatal ? 'error' : 'info', text: ev.message }];
        break;
      case 'done':
        if (last && last.role === 'assistant' && last.streaming) {
          turns[turns.length - 1] = { ...last, streaming: false };
          patch.turns = turns;
        }
        break;
    }
    set(patch);
  },
}));

/** Optimistic user turn so the panel shows the question before the server echoes it. */
export function pushUserTurn(text: string): void {
  const s = useAgent.getState();
  useAgent.setState({ turns: [...s.turns, { id: `local_${Date.now()}`, role: 'user', text, calls: [], streaming: false, createdAt: new Date().toISOString() }], status: 'RUNNING' });
}
